import { useState, useCallback, useRef } from 'react';
import { isValidEmail, isValidPhone } from '@/lib/validation';

/**
 * Shared form submission hook for all portable forms.
 *
 * Submits to the PHP backend at the configured FORM_ENDPOINT.
 *
 * Usage:
 *   const { status, errorMsg, submit, reset } = useFormSubmit({ formType: 'contact' });
 *   <form onSubmit={(e) => submit(e)}>...</form>
 */

export type FormStatus = 'idle' | 'loading' | 'success' | 'error';

export interface FormSubmitOptions {
  /** Which PHP handler to target: contact | feasibility-report | newsletter */
  formType: 'contact' | 'feasibility-report' | 'newsletter';
  /** Override the default endpoint (falls back to VITE_PUBLIC_FORM_ENDPOINT) */
  endpoint?: string;
  /** Callback on successful submission */
  onSuccess?: () => void;
  /** Callback on error */
  onError?: (message: string) => void;
  /** Max length for textarea fields (default 500) */
  maxMessageLength?: number;
}

export interface FormSubmitResult {
  status: FormStatus;
  errorMsg: string;
  charCount: number;
  setCharCount: (n: number) => void;
  submit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  reset: () => void;
}

const DEFAULT_ENDPOINT = '/forms';

function getFormUrl(formType: string, endpoint?: string): string {
  const base = endpoint || (import.meta as Record<string, unknown>).env?.VITE_PUBLIC_FORM_ENDPOINT || DEFAULT_ENDPOINT;
  const cleanBase = (base as string).replace(/\/+$/, '');
  return `${cleanBase}/${formType === 'feasibility-report' ? 'feasibility-report' : formType}.php`;
}

export function useFormSubmit(options: FormSubmitOptions): FormSubmitResult {
  const { formType, endpoint, onSuccess, onError, maxMessageLength = 500 } = options;
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement | null>(null);

  const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    formRef.current = form;
    const data = new FormData(form);

    // Honeypot: silently accept bot submissions locally (no network call)
    const honeypotNames = ['website_alt', 'phone_alt', 'mobile_alt', 'contact_alt', 'company_alt'];
    let honeypotFilled = false;
    for (const n of honeypotNames) {
      const v = data.get(n);
      if (typeof v === 'string' && v.trim()) {
        honeypotFilled = true;
        break;
      }
    }
    if (honeypotFilled) {
      setStatus('success');
      form.reset();
      setCharCount(0);
      onSuccess?.();
      return;
    }

    // Validate textarea limit
    const message = data.get('message') as string;
    if (message && message.length > maxMessageLength) {
      setStatus('error');
      setErrorMsg(`Message must not exceed ${maxMessageLength} characters.`);
      return;
    }

    // Validate email format if present
    const email = (data.get('email') as string) || '';
    if (email && !isValidEmail(email)) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    // Validate phone format if present — catches emails typed into the phone field
    const phone = (data.get('phone') as string) || (data.get('mobile') as string) || '';
    if (phone && !isValidPhone(phone)) {
      setStatus('error');
      setErrorMsg('Please enter a valid phone number (7-15 digits, no letters or @).');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    const body = new URLSearchParams();
    data.forEach((val, key) => {
      body.append(key, val as string);
    });

    const url = getFormUrl(formType, endpoint);

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (!res.ok) {
        const serverMsg = (parsed?.message as string) || `The form server returned an error (HTTP ${res.status}).`;
        setStatus('error');
        setErrorMsg(serverMsg);
        onError?.(serverMsg);
        return;
      }

      // Success
      setStatus('success');
      form.reset();
      setCharCount(0);
      onSuccess?.();
    } catch {
      const msg = 'Network error. Please check your connection and try again.';
      setStatus('error');
      setErrorMsg(msg);
      onError?.(msg);
    }
  }, [formType, endpoint, onSuccess, onError, maxMessageLength]);

  const reset = useCallback(() => {
    setStatus('idle');
    setErrorMsg('');
    setCharCount(0);
    if (formRef.current) {
      formRef.current.reset();
    }
  }, []);

  return { status, errorMsg, charCount, setCharCount, submit, reset };
}